import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, MapPinOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SUB_CLS = {
  active: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  none: "bg-slate-500/15 text-slate-300 border border-slate-500/30",
};

export default function OutsideZonePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/advertisers/outside-zones");
      setItems(data.items || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="outside-zone-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-xl">Outside-Zone Report</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">Advertisers whose business location falls inside no active zone — or who have no location at all. They onboarded outside every territory and are attributed 100% to Velopaths, so review them to create or extend a zone before they slip through.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="outside-zone-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="outside-zone-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <div className="font-heading font-bold">Every advertiser is inside a zone</div>
          <p className="text-sm text-muted-foreground mt-1">No advertisers are onboarded outside your zones right now.</p>
        </div>
      ) : (
        <>
          <div className="text-sm text-muted-foreground" data-testid="outside-zone-count">{items.length} advertiser{items.length === 1 ? "" : "s"} outside all zones</div>
          <div className="rounded-2xl border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Business</TableHead><TableHead>Email</TableHead><TableHead>Issue</TableHead>
                <TableHead>Location</TableHead><TableHead>Stale Zone</TableHead>
                <TableHead>Listing</TableHead><TableHead>Subscription</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} data-testid={`outside-zone-row-${a.id}`}>
                    <TableCell className="font-medium">{a.business_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{a.email || "—"}</TableCell>
                    <TableCell>
                      <Badge className={a.reason === "no_location" ? "bg-rose-500/15 text-rose-300 border border-rose-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"} data-testid={`outside-zone-reason-${a.id}`}>
                        <MapPinOff className="w-3 h-3 mr-1" />{a.reason === "no_location" ? "No location" : "Outside all zones"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums whitespace-nowrap">{a.has_location ? `${Number(a.latitude).toFixed(5)}, ${Number(a.longitude).toFixed(5)}` : "—"}</TableCell>
                    <TableCell className="text-sm">{a.assigned_zone_name || "—"}</TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{a.listing_status}</span></TableCell>
                    <TableCell><Badge className={SUB_CLS[a.subscription_status] || SUB_CLS.none}>{a.subscription_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
